import dayjs from 'dayjs';
import { forwardRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Bar, BarChart, CartesianGrid, Legend, Rectangle, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { ContentPlaceholder } from '../../../../../../components/content-placeholder/content-placeholder';
import { useStrategyStore, useThroughputTabStore } from '../../../../../../store/strategy-store/strategy-store';
import {
  setPerformanceActivePanel,
  setThroughputSelectedDate,
  setThroughputSelectedInitiative,
} from '../../../../../../store/strategy-store/strategy-store.actions';
import { Panel } from '../../../../../../store/strategy-store/strategy-store.type';
import { skyLight, skyLightest } from '../../../../../../styles/design-tokens';
import { getMetricLabel } from '../../../../strategy.helpers';
import { CHART_COLORS } from '../../../initiative-performance.constants';
import { ContributingInitiativesChartLabels } from './contributing-initiatives-chart-labels';
import { ContributingInitiativesChartLegend } from './contributing-initiatives-chart-legend';
import { ContributingInitiativesChartTooltip } from './contributing-initiatives-chart-tooltip';
import { useChartData } from './contributing-initiatives-chart.hooks';

export const ContributingInitiativesChart = forwardRef<HTMLDivElement>((_, ref) => {
  const { t } = useTranslation();
  const initiative = useStrategyStore((state) => state.initiativePerformance);
  const selectedInitiatives = useThroughputTabStore((state) => state.selectedInitiatives);
  const metric = useThroughputTabStore((state) => state.metric);

  const barColors = Object.fromEntries(
    selectedInitiatives.map((initiative, index) => {
      return [initiative.id, CHART_COLORS[index]];
    }),
  );

  const [hoveredItem, setHoveredItem] = useState<string | null>(null);

  const chartData = useChartData();
  const hasData = Array.isArray(chartData) && chartData.length > 0;

  if (!initiative || !hasData) {
    return <ContentPlaceholder />;
  }

  if (selectedInitiatives.length === 0) {
    return <ContentPlaceholder noFiltersApplied />;
  }

  return (
    <div ref={ref} style={{ width: '100%', height: '100%', minHeight: 160 }}>
      <ResponsiveContainer width="100%" height="100%" minHeight={160}>
        <BarChart data={chartData} margin={{ top: 20 }} barGap={2} barCategoryGap="10%">
          <CartesianGrid strokeDasharray="3 3" vertical={false} stroke={skyLight} />
          <XAxis
            dataKey="date"
            tick={{ fontSize: 12 }}
            axisLine={{ stroke: skyLight }}
            tickLine={{ stroke: skyLight }}
            tickFormatter={(value) => dayjs(value).format('MMM, YYYY')}
          />
          <YAxis
            tick={{ fontSize: 12 }}
            axisLine={{ stroke: skyLight }}
            tickLine={{ stroke: skyLight }}
            label={{
              value: `${getMetricLabel(metric, t)} ${t('strategy.initiative_performance.chart_labels.per_month')}`,
              angle: -90,
              position: 'insideLeft',
              style: { textAnchor: 'middle' },
            }}
          />
          <Tooltip
            content={<ContributingInitiativesChartTooltip barColors={barColors} initiatives={selectedInitiatives} />}
            cursor={{ fill: skyLightest }}
          />
          <Legend
            content={
              <ContributingInitiativesChartLegend
                initiatives={selectedInitiatives}
                hoveredItem={hoveredItem}
                setHoveredItem={setHoveredItem}
                barColors={barColors}
              />
            }
            verticalAlign="bottom"
            height={36}
          />
          {selectedInitiatives.map((initiative) => {
            const color = barColors[initiative.id];

            return (
              <Bar
                key={initiative.key}
                dataKey={initiative.id}
                label={
                  hoveredItem === null || hoveredItem === initiative.id ? (
                    <ContributingInitiativesChartLabels />
                  ) : undefined
                }
                width={10}
                animationDuration={0}
                background={{ fill: color, opacity: 0.1 }}
                onClick={(chartData) => {
                  setThroughputSelectedInitiative(initiative);
                  setThroughputSelectedDate(dayjs((chartData as any).date, 'YYYY-MM').toDate());
                  setPerformanceActivePanel(Panel.Side);
                }}
                shape={
                  <Rectangle
                    fill={color}
                    onMouseEnter={() => setHoveredItem(initiative.id)}
                    onMouseLeave={() => setHoveredItem(null)}
                    opacity={hoveredItem === null || hoveredItem === initiative.id ? 0.8 : 0.2}
                    style={{
                      cursor: hoveredItem === initiative.id ? 'pointer' : 'default',
                    }}
                  />
                }
              />
            );
          })}
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
});
